import { useState, useCallback, useRef } from "react";

type CompareFn<T> = (current: T, original: T) => boolean;

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function shallowEqualObject(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);

    if (aKeys.length !== bKeys.length) return false;

    return aKeys.every((key) => a[key] === b[key]);
}

function shallowEqualArray(a: readonly unknown[], b: readonly unknown[]) {
    if (a.length !== b.length) return false;

    return a.every((v, i) => v === b[i]);
}

function defaultHasChanged<T>(current: T, original: T): boolean {
    if (Object.is(current, original)) return false;

    if (Array.isArray(current) && Array.isArray(original)) {
        return !shallowEqualArray(current, original);
    }

    if (isPlainObject(current) && isPlainObject(original)) {
        return !shallowEqualObject(current, original);
    }

    return true;
}

export function useChangeDetection<T>(initialValue: T) {
    const [value, setValue] = useState(initialValue);
    const originalValueRef = useRef(initialValue);

    const hasChanged = useCallback(
        (compareFn?: CompareFn<T>) => {
            const original = originalValueRef.current;

            return compareFn ? compareFn(value, original) : defaultHasChanged(value, original);
        },
        [value],
    );

    const setOriginalValue = useCallback((newOriginal: T) => {
        originalValueRef.current = newOriginal;
    }, []);

    const reset = useCallback(() => {
        setValue(originalValueRef.current);
    }, []);

    return {
        value,
        setValue,
        originalValue: originalValueRef.current,
        hasChanged,
        setOriginalValue,
        reset,
    };
}
